import React, { useState, useMemo } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { SearchIcon } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import {
  calculateUnits,
  getTierFee,
  getTierForMonthlyUnits,
} from './calculateUnits';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from 'recharts';

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($startDate: Date, $endDate: Date) {
    calaimNotes(startDate: $startDate, endDate: $endDate) {
      id
      participant
      firstName
      lastName
      procedurecode
      proceduremodifier
      procedurelength
      serviceStartDate
    }
  }
`;

function getProcedureKey(note: any): string {
  const code = note.procedurecode.trim().toUpperCase();
  const mod = note.proceduremodifier?.trim().toUpperCase() || '';
  return mod ? `${code} ${mod}` : code;
}

function getServiceDate(note: any): string {
  return note.serviceStartDate.split('T')[0];
}

function getMonthLabel(code: string) {
  const [year, month] = code.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' });
}

type TierCounts = {
  tier1: number;
  tier2: number;
  tier3: number;
};

export const PUPM: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [fetchNotes, { data, error }] = useLazyQuery(GET_CALAIM_NOTES);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMonthCode, setSelectedMonthCode] = useState<string>('');
  const [selectedMonthName, setSelectedMonthName] = useState<string>('');

  const notes = useMemo(() => (data?.calaimNotes ?? []) as any[], [data]);

  const groupedUnits = useMemo(() => {
    const groupedMinutes: Record<string, number> = {};

    notes.forEach((note) => {
      const serviceDate = getServiceDate(note);
      const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;
      groupedMinutes[key] = (groupedMinutes[key] || 0) + Number(note.procedurelength || 0);
    });

    return Object.entries(groupedMinutes).reduce((units, [key, totalMinutes]) => {
      units[key] = calculateUnits(totalMinutes);
      return units;
    }, {} as Record<string, number>);
  }, [notes]);

  const feesByMonth = useMemo(() => {
    const participantMonthUnits: Record<string, number> = {};

    Object.entries(groupedUnits).forEach(([key, units]) => {
      if (!units) {
        return;
      }

      const [participant, serviceDate] = key.split('|');
      const monthCode = serviceDate.slice(0, 7);
      const participantMonthKey = `${participant}|${monthCode}`;
      participantMonthUnits[participantMonthKey] =
        (participantMonthUnits[participantMonthKey] || 0) + units;
    });

    const monthFees: Record<string, number> = {};
    const monthUnitTotals: Record<string, number> = {};
    const tierCounts: Record<string, TierCounts> = {};
    let totalTier1 = 0;
    let totalTier2 = 0;
    let totalTier3 = 0;

    Object.entries(participantMonthUnits).forEach(([participantMonthKey, totalUnits]) => {
      const [, monthCode] = participantMonthKey.split('|');
      monthUnitTotals[monthCode] = (monthUnitTotals[monthCode] || 0) + totalUnits;

      const tier = getTierForMonthlyUnits(totalUnits);
      if (!tier) {
        return;
      }

      monthFees[monthCode] = (monthFees[monthCode] || 0) + getTierFee(tier);
      tierCounts[monthCode] ||= { tier1: 0, tier2: 0, tier3: 0 };

      if (tier === 1) {
        tierCounts[monthCode].tier1 += 1;
        totalTier1 += 1;
        return;
      }

      if (tier === 2) {
        tierCounts[monthCode].tier2 += 1;
        totalTier2 += 1;
        return;
      }

      tierCounts[monthCode].tier3 += 1;
      totalTier3 += 1;
    });

    const monthCodes = Array.from(
      new Set(notes.map((note) => getServiceDate(note).slice(0, 7)))
    ).sort();

    const dataArray = monthCodes.map((code) => ({
      code,
      month: getMonthLabel(code),
      totalUnits: monthUnitTotals[code] || 0,
      fee: monthFees[code] || 0,
    }));

    const totalFees = dataArray.reduce((sum, item) => sum + item.fee, 0);
    const totalUnits = dataArray.reduce((sum, item) => sum + item.totalUnits, 0);

    return {
      data: dataArray,
      totalFees,
      totalUnits,
      tierCounts,
      totalTier1,
      totalTier2,
      totalTier3,
    };
  }, [groupedUnits, notes]);

  const rawDataForSelectedMonth = useMemo(
    () =>
      notes.filter((note) => getServiceDate(note).slice(0, 7) === selectedMonthCode),
    [notes, selectedMonthCode]
  );

  const handleSearch = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    fetchNotes({ variables: { startDate, endDate } });
  };

  const handleDownloadAll = () => {
    if (!notes.length) {
      toast.error('No data to download');
      return;
    }

    const rows = notes.map((note) => {
      const serviceDate = getServiceDate(note);
      const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;
      return {
        id: note.id,
        participant: note.participant,
        firstName: note.firstName,
        lastName: note.lastName,
        code: note.procedurecode,
        modifier: note.proceduremodifier,
        length: note.procedurelength,
        date: serviceDate,
        units: groupedUnits[key] || 0,
      };
    });

    const header = ['ID', 'Participant', 'First Name', 'Last Name', 'Code', 'Modifier', 'Length', 'Date', 'Units'];
    const csv = [
      header.join(','),
      ...rows.map((row) =>
        [
          row.id,
          row.participant,
          row.firstName,
          row.lastName,
          row.code,
          row.modifier,
          row.length,
          row.date,
          row.units,
        ]
          .map((value) => `"${value}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'raw_data.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="flex gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button onClick={handleSearch} className="flex items-center gap-2">
          <SearchIcon size={16} />
          <span>Search</span>
        </Button>
        <Button onClick={handleDownloadAll}>
          Download All Raw Data
        </Button>
      </div>

      {error && <div className="text-red-600">Error: {error.message}</div>}

      {data?.calaimNotes ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                {feesByMonth.data.map((item) => (
                  <TableHead key={item.code}>
                    <button
                      className="text-blue-600 underline"
                      onClick={() => {
                        setSelectedMonthCode(item.code);
                        setSelectedMonthName(item.month);
                        setModalIsOpen(true);
                      }}
                    >
                      {item.month}
                    </button>
                  </TableHead>
                ))}
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Total Units</TableCell>
                {feesByMonth.data.map((item) => (
                  <TableCell key={item.code}>{item.totalUnits}</TableCell>
                ))}
                <TableCell>{feesByMonth.totalUnits}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tier 1 Count</TableCell>
                {feesByMonth.data.map((item) => (
                  <TableCell key={item.code}>
                    {feesByMonth.tierCounts[item.code]?.tier1 || 0}
                  </TableCell>
                ))}
                <TableCell>{feesByMonth.totalTier1}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tier 2 Count</TableCell>
                {feesByMonth.data.map((item) => (
                  <TableCell key={item.code}>
                    {feesByMonth.tierCounts[item.code]?.tier2 || 0}
                  </TableCell>
                ))}
                <TableCell>{feesByMonth.totalTier2}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tier 3 Count</TableCell>
                {feesByMonth.data.map((item) => (
                  <TableCell key={item.code}>
                    {feesByMonth.tierCounts[item.code]?.tier3 || 0}
                  </TableCell>
                ))}
                <TableCell>{feesByMonth.totalTier3}</TableCell>
              </TableRow>
              <TableRow className="font-semibold">
                <TableCell>Amount Paid</TableCell>
                {feesByMonth.data.map((item) => (
                  <TableCell key={item.code}>
                    {item.fee.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                ))}
                <TableCell>
                  {feesByMonth.totalFees.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <div className="mt-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[...feesByMonth.data, { month: 'Total', fee: feesByMonth.totalFees }]}
                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" label={{ value: 'Month', position: 'insideBottom', offset: -5 }} />
                <YAxis allowDecimals={false} label={{ value: 'Amount Paid', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value: number) => [value, 'Amount Paid']} />
                <Bar dataKey="fee" fill="#8884d8">
                  <LabelList dataKey="fee" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <Dialog
            open={modalIsOpen}
            onOpenChange={(open: boolean) => {
              if (!open) {
                setModalIsOpen(false);
              }
            }}
          >
            <DialogContent
              className="max-w-none overflow-auto"
              style={{ width: '80%', height: '80%', margin: 'auto' }}
              onInteractOutside={(event) => event.preventDefault()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Raw Data for {selectedMonthName}</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Participant</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Modifier</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Units</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rawDataForSelectedMonth.map((note) => {
                    const serviceDate = getServiceDate(note);
                    const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;

                    return (
                      <TableRow key={note.id}>
                        <TableCell>{note.id}</TableCell>
                        <TableCell>{note.participant}</TableCell>
                        <TableCell>{note.firstName}</TableCell>
                        <TableCell>{note.lastName}</TableCell>
                        <TableCell>{note.procedurecode}</TableCell>
                        <TableCell>{note.proceduremodifier}</TableCell>
                        <TableCell>{note.procedurelength}</TableCell>
                        <TableCell>{serviceDate}</TableCell>
                        <TableCell>{groupedUnits[key] || 0}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="p-4 text-center text-muted-foreground">
          Set date range and click Search
        </div>
      )}
    </div>
  );
};
